import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween'
import DayjsAdapter from '@mui/lab/AdapterDayjs';
import i18next from 'i18next';
import CameraSelection from '../CameraSelection';
import DownloadButton from '../DownloadButton';
import { useEffect, useRef, useState } from 'react';
import { Box, TextField, ToggleButton, ToggleButtonGroup, Alert, FormControlLabel, Checkbox, Typography, Divider, Tooltip, Button } from '@mui/material';
import { StaticDateRangePicker, LocalizationProvider, CalendarPickerSkeleton, DatePicker } from '@mui/lab';
import { getData } from "../../api/dataSlice"
import { useSelector } from "react-redux"
import { useTranslation } from 'react-i18next';
import { useKeycloak } from '../Keycloak';
import { ConditionalWrapper, StyledSlider } from '../Utilities';
import { Download as DownloadIcon } from "@mui/icons-material";

dayjs.extend(isBetween)

export default function Download(props) {

    const { t } = useTranslation()
    const { timestamp, siderWidth, shouldDisableDate, loading: isLoadingParent } = props
    const { source, download, generation } = useSelector(getData)
    const { CAMERAS, DEBUG, LAYOUT: { TITLE, PROJECT_FILE_DATE, PROJECT_FINISHED }, BASEURL } = window.conf
    const { isLoggedIn, isUsersPortal, hasRole } = useKeycloak()

    const [forceRefresh, setForceRefresh] = useState(true)
    const [selectedCamera, setSelectedCamera] = useState(CAMERAS?.[0]?.id)
    const [dateRange, setDateRange] = useState([timestamp, timestamp])
    const [predefinedRange, setPredefinedRange] = useState("")
    const [alertMessage, setAlertMessage] = useState(null)
    const [startDateValid, setStartDateValid] = useState(true)
    const [endDateValid, setEndDateValid] = useState(true)
    const [mode, setMode] = useState({ type: "timelapse", value: "custom" })
    const [useRecommendedLength, setUseRecommendedLength] = useState(true)
    const [maxLength, setMaxLength] = useState(120)
    const [availableResolution, setAvailableResolution] = useState({ original: { width: 1920, height: 1080 }, reduced: { width: 1280, height: 720 } })
    const [selectedResolution, setSelectedResolution] = useState({ width: availableResolution.original.width, height: availableResolution.original.height })

    const daySize = 34
    const today = dayjs()
    const refDownloadButton = useRef()
    const isDownloadRangeExceeded = Math.abs(dateRange?.[0]?.diff(dateRange?.[1], 'day')) > 31
    const isAuthorized = isLoggedIn() && isUsersPortal()
    const isFinished = PROJECT_FILE_DATE && PROJECT_FINISHED
    const minDate = dayjs(source?.[0]?.[0]?.[0])
    const maxDate = dayjs(source?.[0]?.[source[0].length-1]?.[0])
    const moreThanOneCam = CAMERAS.length > 1
    const disableControls = isLoadingParent || download?.loading || generation?.loading || selectedCamera == null
    const disableDownloadTotalImages = !isFinished || !isAuthorized || !hasRole("download") // for complete download finished projects
    const disableDownloadCustomImages = !source?.[0]?.length || selectedCamera == null || isDownloadRangeExceeded || !startDateValid || !endDateValid
    const disableDownloadCustomTimelapse = disableDownloadCustomImages || !isAuthorized

    const camera = CAMERAS[selectedCamera].name
        ? `${CAMERAS[selectedCamera].name}`
        : moreThanOneCam
            ? `${t("events.camera")} ${CAMERAS[selectedCamera].id + 1}`
            : ""
    const title = TITLE
        ? camera
            ? `${TITLE}_`
            :  TITLE
        : ""
    let startDate = dateRange?.[0]?.format("YY-MM-DD")
    const endDate = dateRange?.[1]?.format("YY-MM-DD")
    if (startDate !== endDate) startDate += "_" + endDate
    const targetFilenameTotal = `${title}${camera}`
    const targetFilenameCustom = targetFilenameTotal + `_${startDate}`

    // set default date range depending on selected timestamp of parent components (e.g. archive)
    useEffect(() => {
        if (dateRange?.[0] !== timestamp || dateRange?.[1] !== timestamp) {
            setDateRange([timestamp, timestamp])
            setForceRefresh(!forceRefresh) // force render of StaticDateRangePicker
        }
    }, [timestamp]) // eslint-disable-line react-hooks/exhaustive-deps

    // update available resolution based on selected camera
    useEffect(() => {
        let cancelled = false
        async function getImageResolution() {
            const img = new Image()
            img.src = CAMERAS[selectedCamera].image

            try {
                await img.decode()
                if (cancelled) return
                const original = { width: img.naturalWidth, height: img.naturalHeight }
                const reduced = { width: Math.round(img.naturalWidth / 1.5), height: Math.round(img.naturalHeight / 1.5) }
                DEBUG && console.log(`[Download] Resolution original ${original.width}x${original.height}, reduced ${reduced.width}x${reduced.height}`)
                setAvailableResolution({ original, reduced })
                setSelectedResolution({ width: original.width, height: original.height })
            } catch (error) {
                console.error("[Download]", error)
            }
        }
        getImageResolution()
        return () => { cancelled = true }
    }, [selectedCamera]) // eslint-disable-line react-hooks/exhaustive-deps

    // scroll to download button if alert message is shown e.g. timelapse generation started
    useEffect(() => {
        if (alertMessage) {
            refDownloadButton.current?.scrollIntoView({ behavior: "smooth", block: "center" })
        }
    }, [alertMessage])

    useEffect(()=> {
        if (isDownloadRangeExceeded) {
            setAlertMessage({ type: "warning", message: t("msg.select_one_month") })
        }
    }, [isDownloadRangeExceeded]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (mode.value === "total" && mode.type === "images" && !isFinished) {
            setAlertMessage({ type: "info", message: t("msg.after_finish") })
        }
    }, [mode, isFinished]) // eslint-disable-line react-hooks/exhaustive-deps

    function resetAlertMessage() {
        alertMessage && setAlertMessage(null)
    }

    const onChangeModeType = (event, newType) => {
        resetAlertMessage()
        if (newType !== null) {
            setMode({ ...mode, type: newType })
        }
    }

    const onChangeModeValue = (event, newValue) => {
        resetAlertMessage()
        if (newValue !== null) {
            setMode({ ...mode, value: newValue })
        }
    }

    const onChangeSelectedCamera = value => {
        setSelectedCamera(value)
    }

    const onChangeDateRange = (dateRange) => {
        resetAlertMessage()
        if (dateRange?.length) {
            setDateRange([dateRange[0], dateRange[1]])
            setPredefinedRange("")
        }
    }

    const onChangeStartDate = (startDate) => {
        resetAlertMessage()
        if (startDate) {
            setDateRange([startDate, dateRange[1]])
            setPredefinedRange("")
        }
    }

    const onChangeEndDate = (endDate) => {
        resetAlertMessage()
        if (endDate) {
            setDateRange([dateRange[0], endDate])
            setPredefinedRange("")
        }
    }

    const onChangePredefinedSpan = (event, newPredefinedSpan) => {
        resetAlertMessage()
        if (newPredefinedSpan) {
            switch (newPredefinedSpan) {
                case "day":
                    const yesterday = today.subtract(1, 'day')
                    setDateRange([yesterday, yesterday])
                    break;
                case "week":
                    var lastWeek = today.subtract(1, 'week')
                    setDateRange([lastWeek?.day(1), lastWeek?.day(7)])
                    break;
                case "month":
                    var lastMonth = today.subtract(1, 'month')
                    setDateRange([lastMonth?.date(1), lastMonth?.endOf("month")])
                    break;
                default:
                    break;
            }

            setPredefinedRange(newPredefinedSpan)
            setForceRefresh(!forceRefresh) // force render of StaticDateRangePicker
        }
    }

    const onChangeUseRecommendedLength = (event) => {
        resetAlertMessage()
        setUseRecommendedLength(event.target.checked)
    }

    const onChangeMaxLength = (event, newValue) => {
        resetAlertMessage()
        setMaxLength(newValue)
    }

    const onChangeSelectedResolution = (event, newValue) => {
        resetAlertMessage()
        if (newValue === 1) {
            setSelectedResolution(availableResolution.reduced)
        } else if (newValue === 2) {
            setSelectedResolution(availableResolution.original)
        }
    }

    return (
        <Box>
            <ToggleButtonGroup
                color="primary"
                exclusive
                onChange={onChangeModeType}
                value={mode.type}
                fullWidth
                sx={{ pb: 2 }}
                disabled={disableControls}
            >
                <ToggleButton value="timelapse">
                    {t("pages.timelapse")}
                </ToggleButton>

                <ToggleButton value="images">
                    {t("download.images")}
                </ToggleButton>
                
            </ToggleButtonGroup>

            <ToggleButtonGroup
                color="primary"
                exclusive
                onChange={onChangeModeValue}
                value={mode.value}
                fullWidth
                sx={{ pb: 2 }}
                disabled={disableControls}
            >
                <ToggleButton value="total">
                    {t("download.total")}
                </ToggleButton> 

                <ToggleButton value="custom">
                    {t("download.custom")}
                </ToggleButton>

            </ToggleButtonGroup>

            {moreThanOneCam && !(mode.type === "images" && mode.value === "total") && (
                <CameraSelection
                    value={selectedCamera}
                    onChange={onChangeSelectedCamera}
                    disabled={disableControls}
                />
            )}

            {mode.value === "custom" && (
                <Box>
                    <LocalizationProvider
                        dateAdapter={DayjsAdapter}
                        locale={i18next.language}
                    >
                        <Box sx={{ display: 'flex'}}>
                            <DatePicker
                                label={t("date.start_date")}
                                inputFormat={t("date.format.short")}
                                mask={t("date.mask.short")}
                                renderInput={props => 
                                    <TextField 
                                        {...props} 
                                        sx={{
                                            width: "100%", 
                                            userSelect: "none",
                                            "& .MuiOutlinedInput-root": {
                                                "& > fieldset": { borderColor: "#4caf50" },
                                            }, 
                                            "& .MuiInputAdornment-root": {
                                                margin: '-4px'
                                            }
                                        }}
                                    />    
                                }
                                onError={reason => reason ? setStartDateValid(false) : setStartDateValid(true)}
                                disableFuture={true}
                                value={dateRange[0]}
                                onChange={onChangeStartDate}
                                cancelText={t("actions.cancel")}
                                toolbarTitle={t("date.select_date")}
                                minDate={minDate}
                                maxDate={dateRange[1]}
                                loading={isLoadingParent}
                                disabled={selectedCamera == null || download?.loading || generation?.loading}
                                renderLoading={() => <CalendarPickerSkeleton />}
                            />
                        
                            <p style={{
                                paddingLeft: 12, 
                                paddingRight: 12, 
                                height: "100%",
                                alignItems: 'center',
                                userSelect: 'none'
                            }}>
                                {t("date.to")}
                            </p>

                            <DatePicker
                                label={t("date.end_date")}
                                inputFormat={t("date.format.short")}
                                mask={t("date.mask.short")}
                                renderInput={props => 
                                    <TextField
                                        {...props} 
                                        sx={{
                                            width: "100%", 
                                            userSelect: "none",
                                            "& .MuiOutlinedInput-root": {
                                                "& > fieldset": { borderColor: "#ed6c02" },
                                            }, 
                                            "& .MuiInputAdornment-root": {
                                                margin: '-4px',
                                            }
                                        }}
                                    />    
                                }
                                onError={reason => reason ? setEndDateValid(false) : setEndDateValid(true)}
                                disableFuture={true}
                                value={dateRange[1]}
                                onChange={onChangeEndDate}
                                cancelText={t("actions.cancel")}
                                toolbarTitle={t("date.select_date")}
                                minDate={dateRange[0]}
                                maxDate={maxDate}
                                loading={isLoadingParent}
                                disabled={selectedCamera == null || download?.loading || generation?.loading}
                                renderLoading={() => <CalendarPickerSkeleton />}
                            />
                        </Box>
                    </LocalizationProvider>
                
                    <Box
                        sx={{
                            "& > div": {
                                minWidth: siderWidth,
                                pt: 2
                            },
                            "& > div > div": {
                                border: "1px solid",
                                borderColor: "#BDBDBD",
                                borderRadius: "4px"
                            },
                            "& > div > div, & > div > div > div, & > div > div > div > div": {
                                width: siderWidth - 2,
                                // minHeight: siderWidth + 24
                            },
                            "& .MuiTypography-caption": {
                                width: daySize + 4,
                                margin: 0
                            },
                            "& .PrivatePickersSlideTransition-root": {
                                minHeight: daySize * 7.2,
                                overflow: "hidden"
                            },
                            '& .PrivatePickersSlideTransition-root [role="grid"]': {
                                width: siderWidth
                            },
                            // "& .MuiPickersDay-dayWithMargin": {
                            //     margin: 0
                            // },
                            "& .MuiPickersDay-root": {
                                width: daySize,
                                height: daySize
                            }
                        }}
                    >
                        <LocalizationProvider 
                            dateAdapter={DayjsAdapter}
                            locale={i18next.language}
                            key={forceRefresh}
                        >
                            <StaticDateRangePicker
                                calendars={1}
                                displayStaticWrapperAs="desktop"
                                mask={t("date.mask.full")}
                                value={dateRange}
                                onChange={onChangeDateRange}
                                renderInput={() => {}}
                                minDate={minDate}
                                maxDate={maxDate}
                                loading={isLoadingParent}
                                disabled={selectedCamera == null || download?.loading || generation?.loading}
                                shouldDisableDate={shouldDisableDate}
                                renderLoading={() => <CalendarPickerSkeleton />}
                            />
                        </LocalizationProvider>
                    </Box>

                    <ToggleButtonGroup
                        color="primary"
                        exclusive
                        onChange={onChangePredefinedSpan}
                        value={predefinedRange}
                        fullWidth
                        orientation='vertical'
                        sx={{ py: 2 }}
                    >
                        <ToggleButton disabled={disableControls || today.subtract(1, 'day') < minDate || today.subtract(1, 'day') > maxDate} value="day">{t("date.yesterday")}</ToggleButton> 
                        <ToggleButton disabled={disableControls || today.subtract(1, 'week').day(1) < minDate || today.subtract(1, 'week').day(1) > maxDate} value="week">{t("date.last_week")}</ToggleButton>
                        <ToggleButton disabled={disableControls || today.subtract(1, 'month').date(1) < minDate || today.subtract(1, 'month').date(1) > maxDate} value="month">{t("date.last_month")}</ToggleButton>
                    </ToggleButtonGroup>

                    {/* custom timelapse controls */}

                    {mode.type === "timelapse" && (
                        <Box sx={{
                            px: 2,
                            py: 1,
                            borderRadius: "4px",
                            border: "1px solid rgba(0, 0, 0, 0.12)",
                            userSelect: "none",
                            mb: 2
                        }}>
                            
                            {/* max length slider */}

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={useRecommendedLength}
                                        onChange={onChangeUseRecommendedLength}
                                        disabled={disableControls}
                                    />
                                }
                                label={
                                    <Box sx={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis"
                                    }}>
                                        {t("timelapse.use_recommended_length")}
                                    </Box>
                                }
                            />
                            
                            <Box sx={{ mt: 1, position: "relative" }}>
                            
                                <Typography sx={{ color: useRecommendedLength || disableControls ? "#bdbdbd" : "text.primary" }}>
                                    {`${t("timelapse.maximum_length")}: ${maxLength} ${t("timelapse.seconds")}`}
                                </Typography>

                                <Box sx={{ p: "0 10px 10px" }}>

                                    <StyledSlider
                                        aria-label={t("timelapse.maximum_length")}
                                        value={maxLength}
                                        getAriaValueText={(value) => `${value}%`}
                                        step={10}
                                        min={10}
                                        max={120}
                                        disabled={useRecommendedLength || disableControls}
                                        valueLabelDisplay="auto"
                                        marks={[{ value: 10, label: "10" }, { value: 120, label: "120"}]}
                                        onChange={onChangeMaxLength}
                                    />

                                    <Box sx={{
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        position: "absolute",
                                        left: 0,
                                        bottom: 10
                                    }}>

                                        <Typography sx={{
                                            fontSize: "0.875rem",
                                            color: useRecommendedLength || disableControls ? "#bdbdbd" : "text.primary"
                                        }}>
                                            10
                                        </Typography>

                                        <Typography sx={{ 
                                            fontSize: "0.875rem",
                                            color: useRecommendedLength || disableControls ? "#bdbdbd" : "text.primary"
                                        }}>
                                            120
                                        </Typography>

                                    </Box>
                                </Box>
                            </Box>

                            <Divider />

                            {/* quality slider */}

                            <Box sx={{ mt: 2, position: "relative" }}>

                                <Typography sx={{ color: disableControls ? "#bdbdbd" : "text.primary"}}>
                                    {t("timelapse.quality")}: {selectedResolution.width}x{selectedResolution.height}
                                </Typography>

                                <Box sx={{ padding: "0 10px" }}>

                                    <StyledSlider
                                        aria-label={t("timelapse.quality")}
                                        value={selectedResolution.width === availableResolution.reduced.width ? 1 : 2}
                                        getAriaValueText={() => `${selectedResolution.width}x${selectedResolution.height}`}
                                        valueLabelFormat={() => `${selectedResolution.width}x${selectedResolution.height}`}
                                        disabled={disableControls}
                                        step={1}
                                        min={1}
                                        max={2}
                                        valueLabelDisplay="auto"
                                        marks={[
                                            { value: 1, label: `${availableResolution.reduced.width}x${availableResolution.reduced.height}` },
                                            { value: 2, label: `${availableResolution.original.width}x${availableResolution.original.width}` }
                                        ]}
                                        onChange={onChangeSelectedResolution}
                                    />

                                    <Box sx={{
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        position: "absolute",
                                        left: 0,
                                        bottom: 0
                                    }}>

                                        <Typography sx={{ 
                                            fontSize: "0.875rem",
                                            color: disableControls ? "#bdbdbd" : "text.primary"
                                        }}>
                                            {t("timelapse.reduced")}
                                        </Typography>

                                        <Typography sx={{ 
                                            fontSize: "0.875rem",
                                            color: disableControls ? "#bdbdbd" : "text.primary"
                                        }}>
                                            {t("timelapse.original")}
                                        </Typography>

                                    </Box>
                                </Box>
                            </Box>

                        </Box>
                    )}
                </Box>
            )}

            {alertMessage && (
                <Box sx={{ mb: 2 }}>
                    <Alert
                        variant="filled"
                        severity={alertMessage.type}
                        sx={{ alignItems: "center", userSelect: "none" }}
                    >
                        {alertMessage.message || t("msg.unknown_error")}
                    </Alert>
                </Box>
            )}

            {mode.type === "timelapse" && mode.value === "total" && (
                <DownloadButton
                    sourceUrl={CAMERAS[selectedCamera]?.timelapse}
                    targetFilename={`${targetFilenameTotal || t("pages.timelapse")}.mp4`}
                    fileType="video"
                    disabled={disableControls}
                />
            )}

            {mode.type === "images" && mode.value === "total" && (
                <ConditionalWrapper
                    condition={!isAuthorized}
                    wrapper={children => <Tooltip title={t("msg.please_login")} placement="top" arrow>{children}</Tooltip>}
                >

                    <Button
                        component="a"
                        href={`${BASEURL}downloads/${PROJECT_FILE_DATE ^ 9102015}.zip`}
                        download={`${dayjs.unix(PROJECT_FILE_DATE).format("YY-MM-DD")}.zip`}
                        target="_blank"
                        rel="noopener"
                        variant="contained"
                        size="large"
                        startIcon={<DownloadIcon />}
                        sx={{ width: "100%" }}
                        disabled={disableControls || disableDownloadTotalImages}
                        onClick={event => (disableControls || disableDownloadTotalImages) && event.preventDefault()}
                    >
                        {t("actions.download")}
                    </Button>

                </ConditionalWrapper>
            )}

            {mode.value === "custom" && (
                <ConditionalWrapper
                    condition={mode.type === "timelapse" && !isAuthorized}
                    wrapper={children => <Tooltip title={t("msg.please_login")} placement="top" arrow>{children}</Tooltip>}
                >
                    <DownloadButton
                        targetFilename={mode.type === "timelapse"
                            ? `${targetFilenameCustom || t("actions.timelapse")}.mp4`
                            : `${targetFilenameCustom || t("actions.download")}`}
                        disabled={disableControls || 
                            mode.type === "timelapse"
                            ? disableDownloadCustomTimelapse
                            : disableDownloadCustomImages}
                        selectedCamera={selectedCamera}
                        dateRange={dateRange}
                        setAlertMessage={setAlertMessage}
                        ref={refDownloadButton}
                        {...mode.type === "timelapse" && {
                            selectedResolution,
                            maxLength: useRecommendedLength ? 0 : maxLength
                        }}
                    />
                </ConditionalWrapper>
            )}
        </Box>
    )
}